"use client";

import { usePathname } from "next/navigation";
import NavBar, { type MenuItem } from "./ui/navbar";
import { getGrantedEntitlementsFromSubscriptions } from "@/lib/billing/entitlements";
import { signedOutTrialHref } from "@/lib/landing-constants";
import { isHomePath } from "@/lib/marketing/home-arms";
import {
  NAV_SIGNED_IN_VARIANT_CLASS,
  NAV_SIGNED_OUT_VARIANT_CLASS,
} from "@/lib/nav-auth-hint";
import { usePublicAuth } from "@/lib/public-auth";

interface NavbarAuthSlotProps {
  baseMenus: MenuItem[];
  sticky?: boolean;
}

export function NavbarAuthSlot({ baseMenus, sticky = true }: NavbarAuthSlotProps) {
  const { isAuthenticated, isLoading, subscriptions, signOut } = usePublicAuth();
  const pathname = usePathname();

  const entitlements = getGrantedEntitlementsFromSubscriptions(subscriptions);
  const applyAppUrls = (items: MenuItem[]): MenuItem[] =>
    items.map((item) => ({
      ...item,
      url:
        item.appUrl && (!item.appProduct || entitlements.has(item.appProduct))
          ? item.appUrl
          : item.url,
      items: item.items ? applyAppUrls(item.items) : item.items,
    }));

  const buildList = (authenticated: boolean): MenuItem[] => {
    const fullMenus = authenticated ? applyAppUrls(baseMenus) : baseMenus;
    const menus =
      isHomePath(pathname) && !authenticated
        ? fullMenus
            .filter((item) => ["/tools", "/pricing", "/community", "/learning-center"].includes(item.url))
            .map((item) =>
              item.url === "/learning-center" ? { ...item, title: "Resources" } : item,
            )
        : fullMenus;

    const authMenuItems: MenuItem[] = authenticated
      ? [
          {
            id: 6,
            title: "Account",
            url: "/account",
            dropdown: true,
            items: [
              {
                id: 61,
                title: "Subscription",
                url: "/account",
                dropdown: false,
              },
              {
                id: 62,
                title: "Sign Out",
                url: "#",
                dropdown: false,
                onClick: signOut,
              },
            ],
          },
        ]
      : [
          {
            id: 7,
            title: "Sign In",
            url: "/signin",
            dropdown: false,
          },
          {
            id: 8,
            title: "Start free trial",
            url: signedOutTrialHref(pathname),
            dropdown: false,
          },
        ];

    return [...menus, ...authMenuItems];
  };

  if (isLoading) {
    return (
      <>
        <NavBar
          key="anon"
          list={buildList(false)}
          isAuthenticated={false}
          sticky={sticky}
          className={NAV_SIGNED_OUT_VARIANT_CLASS}
        />
        <NavBar
          key="auth"
          list={buildList(true)}
          isAuthenticated
          sticky={sticky}
          className={NAV_SIGNED_IN_VARIANT_CLASS}
        />
      </>
    );
  }

  return (
    <NavBar
      key={isAuthenticated ? "auth" : "anon"}
      list={buildList(isAuthenticated)}
      isAuthenticated={isAuthenticated}
      sticky={sticky}
    />
  );
}
